import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { regionalInPersonAPI, purchaseOrdersAPI, taxonomyAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { ContentItemLinkDialog } from '../components/content-items/ContentItemLinkDialog';
import { POLineItemSelect } from '../components/purchase-orders/POLineItemSelect';
import { EntityCustomFields } from '../components/shared/EntityCustomFields';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Save, Loader2, Layers, X } from 'lucide-react';
import { toast } from 'sonner';

const REGIONS = ['Asia', 'Americas', 'EMEA', 'MEA', 'Other'];
const WORKLOADS = ['Security', 'Agentic AI', 'Copilot', 'Fabric', 'Modernize Apps', 'Sovereign Cloud', 'Other'];
const LANGUAGES = ['English', 'Spanish', 'Portuguese', 'French', 'German', 'Italian', 'Japanese', 'Korean', 'Chinese (Simplified)', 'Chinese (Traditional)', 'Turkish', 'Arabic', 'Other'];
const COURSE_TYPES = ['TDR', 'PR', 'TDR+PR'];
const DELIVERY_STATUSES = ['Scheduled', 'Delivered', 'Cancelled', 'Postponed', 'Converted to Virtual'];
const POE_OPTIONS = ['Yes', 'No', 'Pending'];
const TRAVEL_PLAN_OPTIONS = ['Shared', 'Awaited'];

const formatCurrency = (v) => `$${(v || 0).toLocaleString()}`;

const TABS = ['Event Details', 'Trainer Details', 'Instructor Resources', 'Connect Calls'];

const EMPTY = {
  // Tab 1 - Event Details
  title: '', city: '', country: '', region: '', workload: '',
  training_start_date: '', training_end_date: '', delivery_days: 1,
  course_type: '', delivery_language: 'English', content_labs_language: 'English',
  po_number: '', delivery_status: 'Scheduled', poe_shared: 'No',
  po_line_item_id: '', po_line_item_description: '',
  approved_price: '', amount_invoiced: '',
  content_item_id: '', content_item_title: '',
  prospective_trainers: '', notes: '', description: '',
  // Tab 2 - Trainer Details
  trainer1_name: '', trainer1_contract_no: '', trainer1_email: '',
  trainer1_arrival_date: '', trainer1_travel_plan: '', trainer1_readiness: false,
  trainer2_name: '', trainer2_contract_no: '', trainer2_email: '',
  trainer2_arrival_date: '', trainer2_travel_plan: '', trainer2_readiness: false,
  // Tab 3 - Instructor Resources
  rio_content_labs_status: 'Pending', rio_lab_key_status: 'No',
  event_venue_shared: false, poc_details_shared: false, registration_pages_shared: false,
  instructor_remarks: '',
  // Tab 4 - Connect Calls
  internal_call_date: '', ms_call_date: '',
  custom_fields: {},
};

export const RegionalInPersonFormPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allPos, setAllPos] = useState([]);
  const [contentItemDialogOpen, setContentItemDialogOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [customFieldKeys, setCustomFieldKeys] = useState([]);

  useEffect(() => {
    purchaseOrdersAPI.getAll({ active_only: true }).then(setAllPos).catch(() => {});
    taxonomyAPI.getAll().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    regionalInPersonAPI.getById(id)
      .then(data => {
        setForm({
          // Tab 1
          title: data.title || '',
          city: data.city || '',
          country: data.country || '',
          region: data.region || '',
          workload: data.workload || '',
          training_start_date: data.preferred_dates || '',
          training_end_date: data.training_end_date || '',
          delivery_days: data.delivery_days ?? 1,
          course_type: data.course_type || '',
          delivery_language: data.delivery_language || 'English',
          content_labs_language: data.content_labs_language || 'English',
          po_number: data.po_number || '',
          po_line_item_id: data.po_line_item_id || '',
          po_line_item_description: data.po_line_item_description || '',
          approved_price: data.approved_price ?? '',
          amount_invoiced: data.amount_invoiced ?? '',
          delivery_status: data.delivery_status || 'Scheduled',
          poe_shared: data.poe_shared || 'No',
          content_item_id: data.content_item_id || '',
          content_item_title: data.content_item_title || '',
          prospective_trainers: data.prospective_trainers || '',
          notes: data.notes || '',
          description: data.description || '',
          // Tab 2 - Trainer Details
          trainer1_name: data.trainer1_name || '',
          trainer1_contract_no: data.trainer1_contract_no || '',
          trainer1_email: data.trainer1_email || '',
          trainer1_arrival_date: data.trainer1_arrival_date || '',
          trainer1_travel_plan: data.trainer1_travel_plan || '',
          trainer1_readiness: data.trainer1_readiness || false,
          trainer2_name: data.trainer2_name || '',
          trainer2_contract_no: data.trainer2_contract_no || '',
          trainer2_email: data.trainer2_email || '',
          trainer2_arrival_date: data.trainer2_arrival_date || '',
          trainer2_travel_plan: data.trainer2_travel_plan || '',
          trainer2_readiness: data.trainer2_readiness || false,
          // Tab 3 - Instructor Resources
          rio_content_labs_status: data.rio_content_labs_status || 'Pending',
          rio_lab_key_status: data.rio_lab_key_status || 'No',
          event_venue_shared: data.event_venue_shared || false,
          poc_details_shared: data.poc_details_shared || false,
          registration_pages_shared: data.registration_pages_shared || false,
          instructor_remarks: data.instructor_remarks || '',
          // Tab 4 - Connect Calls
          internal_call_date: data.internal_call_date || '',
          ms_call_date: data.ms_call_date || '',
          custom_fields: data.custom_fields || {},
        });
        setCustomFieldKeys(Object.keys(data.custom_fields || {}));
      })
      .catch(() => toast.error('Failed to load event'))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const set = (field, value) => setForm(p => ({ ...p, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Event title is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        approved_price: form.approved_price !== '' ? parseFloat(form.approved_price) : null,
        amount_invoiced: form.amount_invoiced !== '' ? parseFloat(form.amount_invoiced) : null,
      };
      if (isEdit) {
        await regionalInPersonAPI.update(id, payload);
        toast.success('Event updated');
        navigate(`/regional-in-person/${id}`);
      } else {
        const created = await regionalInPersonAPI.create(payload, user.id);
        toast.success('Event created');
        navigate(`/regional-in-person/${created.id}`);
      }
    } catch { toast.error(isEdit ? 'Failed to update' : 'Failed to create'); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-8 h-8 animate-spin text-lime" />
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-heading font-bold">
            {isEdit ? 'Edit Event' : 'New Regional In-Person Event'}
          </h1>
          <p className="text-muted-foreground text-sm">Regional In-Person · Small Tent Events</p>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-0 border-b">
        {TABS.map((t, i) => (
          <button key={t} type="button" onClick={() => setActiveTab(i)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === i ? 'border-lime text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}>
            {t}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit}>
        {/* Tab 1: Event Details */}
        {activeTab === 0 && (
          <>
          <Card>
            <CardHeader><CardTitle className="text-base">Event Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label>Course Title / Event Name <span className="text-destructive">*</span></Label>
                <Input value={form.title} onChange={e => set('title', e.target.value)}
                  placeholder="e.g. Position and specialize Microsoft Sentinel for Modern SOC"
                  data-testid="rio-title" required />
              </div>
              <div className="space-y-1.5">
                <Label>Linked Content Dev Course</Label>
                {form.content_item_id ? (
                  <div className="flex items-center justify-between gap-2 border rounded-lg p-2.5 bg-blue-50 border-blue-200" data-testid="rio-selected-content-item">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{form.content_item_title}</p>
                      <p className="text-xs text-muted-foreground">Content Dev tracker will flag urgency if this course isn't build-complete yet</p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Button type="button" variant="outline" size="sm" onClick={() => setContentItemDialogOpen(true)} data-testid="rio-change-content-item-btn">Change</Button>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setForm(f => ({ ...f, content_item_id: '', content_item_title: '' }))} data-testid="rio-clear-content-item-btn">
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button type="button" variant="outline" className="w-full justify-start" onClick={() => setContentItemDialogOpen(true)} data-testid="rio-open-content-item-search-btn">
                    <Layers className="w-4 h-4 mr-2" />Link to a Content Dev course (optional)...
                  </Button>
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Training Start Date</Label>
                  <Input type="date" value={form.training_start_date} onChange={e => set('training_start_date', e.target.value)} data-testid="rio-start-date" />
                </div>
                <div className="space-y-1.5">
                  <Label>Training End Date</Label>
                  <Input type="date" value={form.training_end_date} onChange={e => set('training_end_date', e.target.value)} data-testid="rio-end-date" />
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label>Delivery Days</Label>
                  <Select value={String(form.delivery_days)} onValueChange={v => set('delivery_days', parseInt(v))}>
                    <SelectTrigger data-testid="rio-delivery-days"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 Day</SelectItem>
                      <SelectItem value="2">2 Days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Course Type</Label>
                  <Select value={form.course_type} onValueChange={v => set('course_type', v)}>
                    <SelectTrigger data-testid="rio-course-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>{COURSE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Workload</Label>
                  <Select value={form.workload} onValueChange={v => set('workload', v)}>
                    <SelectTrigger data-testid="rio-workload"><SelectValue placeholder="Select workload" /></SelectTrigger>
                    <SelectContent>{WORKLOADS.map(w => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label>City</Label>
                  <Input value={form.city} onChange={e => set('city', e.target.value)} placeholder="e.g. Bangkok" data-testid="rio-city" />
                </div>
                <div className="space-y-1.5">
                  <Label>Country</Label>
                  <Input value={form.country} onChange={e => set('country', e.target.value)} placeholder="e.g. Thailand" data-testid="rio-country" />
                </div>
                <div className="space-y-1.5">
                  <Label>Region</Label>
                  <Select value={form.region} onValueChange={v => set('region', v)}>
                    <SelectTrigger data-testid="rio-region"><SelectValue placeholder="Select region" /></SelectTrigger>
                    <SelectContent>{REGIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Delivery Language</Label>
                  <Select value={form.delivery_language} onValueChange={v => set('delivery_language', v)}>
                    <SelectTrigger data-testid="rio-delivery-lang"><SelectValue /></SelectTrigger>
                    <SelectContent>{LANGUAGES.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Content + Labs Language</Label>
                  <Select value={form.content_labs_language} onValueChange={v => set('content_labs_language', v)}>
                    <SelectTrigger data-testid="rio-content-lang"><SelectValue /></SelectTrigger>
                    <SelectContent>{LANGUAGES.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Delivery Status</Label>
                  <Select value={form.delivery_status} onValueChange={v => set('delivery_status', v)}>
                    <SelectTrigger data-testid="rio-delivery-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{DELIVERY_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>POE Shared</Label>
                  <Select value={form.poe_shared} onValueChange={v => set('poe_shared', v)}>
                    <SelectTrigger data-testid="rio-poe"><SelectValue /></SelectTrigger>
                    <SelectContent>{POE_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Financials */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Financials</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Approved Price (USD)</Label>
                  <Input type="number" min="0" step="0.01" value={form.approved_price} onChange={e => set('approved_price', e.target.value)} placeholder="0.00" data-testid="rio-approved-price" />
                </div>
                <div className="space-y-1.5">
                  <Label>Amount Invoiced (USD)</Label>
                  <Input type="number" min="0" step="0.01" value={form.amount_invoiced} onChange={e => set('amount_invoiced', e.target.value)} placeholder="0.00" data-testid="rio-amount-invoiced" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>PO# Invoiced in</Label>
                <Select
                  value={form.po_number || 'none'}
                  onValueChange={v => setForm(f => ({ ...f, po_number: v === 'none' ? '' : v, po_line_item_id: '', po_line_item_description: '' }))}
                >
                  <SelectTrigger data-testid="rio-po"><SelectValue placeholder="Select PO" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not Assigned</SelectItem>
                    {allPos.map(po => (
                      <SelectItem key={po.id} value={po.po_number}>
                        <div className="flex items-center justify-between w-full">
                          <span>{po.po_number}{po.short_title ? ` — ${po.short_title}` : ''}</span>
                          <Badge variant="outline" className="text-xs ml-2">{formatCurrency(po.available_balance)} available</Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <POLineItemSelect
                pos={allPos}
                poNumber={form.po_number}
                lineItemId={form.po_line_item_id}
                deliveryType="regional_in_person"
                deliveryDate={form.training_start_date}
                invoiceAmount={form.amount_invoiced || form.approved_price}
                onChange={(id, description) => setForm(f => ({ ...f, po_line_item_id: id, po_line_item_description: description }))}
                testIdPrefix="rio-po-line-item"
              />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-4 pt-6">
              <div className="space-y-1.5">
                <Label>Prospective Trainers</Label>
                <Textarea value={form.prospective_trainers} onChange={e => set('prospective_trainers', e.target.value)}
                  placeholder="Names being considered before confirmation..." rows={2} data-testid="rio-prospective" />
              </div>
            </CardContent>
          </Card>
          </>
        )}

        {/* Tab 2: Trainer Details */}
        {activeTab === 1 && (
          <div className="grid sm:grid-cols-2 gap-4">
            {[1, 2].map(n => (
              <Card key={n}>
                <CardHeader className="pb-3"><CardTitle className="text-sm">Trainer {n}</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {[
                    ['name', 'Full Name', 'text', `e.g. John Smith`],
                    ['contract_no', 'Contract #', 'text', 'e.g. S3851 - signed'],
                    ['email', 'TF Email (ID)', 'email', 'p-name@technofocus.pro'],
                    ['arrival_date', 'Arrival Date', 'date', ''],
                  ].map(([field, label, type, placeholder]) => (
                    <div key={field} className="space-y-1">
                      <Label className="text-xs">{label}</Label>
                      <Input
                        type={type}
                        value={form[`trainer${n}_${field}`] || ''}
                        onChange={e => set(`trainer${n}_${field}`, e.target.value)}
                        placeholder={placeholder}
                        className="h-8 text-sm"
                        data-testid={`rio-trainer${n}-${field}`}
                      />
                    </div>
                  ))}
                  <div className="space-y-1">
                    <Label className="text-xs">Travel Plan</Label>
                    <Select value={form[`trainer${n}_travel_plan`] || ''} onValueChange={v => set(`trainer${n}_travel_plan`, v)}>
                      <SelectTrigger className="h-8 text-sm" data-testid={`rio-trainer${n}-travel`}>
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>{TRAVEL_PLAN_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <Checkbox
                      id={`readiness-${n}`}
                      checked={form[`trainer${n}_readiness`] || false}
                      onCheckedChange={v => set(`trainer${n}_readiness`, v)}
                      data-testid={`rio-trainer${n}-readiness`}
                    />
                    <Label htmlFor={`readiness-${n}`} className="text-xs cursor-pointer">Trainer Ready</Label>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Tab 3: Instructor Resources */}
        {activeTab === 2 && (
          <Card>
            <CardHeader><CardTitle className="text-base">Instructor Resources</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Content + Labs Shared</Label>
                  <Select value={form.rio_content_labs_status || 'Pending'} onValueChange={v => set('rio_content_labs_status', v)}>
                    <SelectTrigger data-testid="rio-content-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['Yes', 'No', 'Pending'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Lab Key Shared</Label>
                  <Select value={form.rio_lab_key_status || 'No'} onValueChange={v => set('rio_lab_key_status', v)}>
                    <SelectTrigger data-testid="rio-lab-key"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['Yes', 'No', 'Pending'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-3 pt-2">
                {[
                  ['event_venue_shared', 'Event Venue Details shared with trainers'],
                  ['poc_details_shared', 'MS Onsite POC details shared'],
                  ['registration_pages_shared', 'Event Registration pages shared'],
                ].map(([field, label]) => (
                  <div key={field} className="flex items-center gap-3">
                    <Checkbox
                      id={field}
                      checked={form[field] || false}
                      onCheckedChange={v => set(field, v)}
                      data-testid={`rio-${field}`}
                    />
                    <Label htmlFor={field} className="text-sm cursor-pointer">{label}</Label>
                  </div>
                ))}
              </div>
              <div className="space-y-1.5">
                <Label>Remarks / Status</Label>
                <Textarea value={form.instructor_remarks || ''} onChange={e => set('instructor_remarks', e.target.value)}
                  placeholder="Any notes on content readiness, lab issues, etc." rows={3} data-testid="rio-instructor-remarks" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tab 4: Connect Calls */}
        {activeTab === 3 && (
          <Card>
            <CardHeader><CardTitle className="text-base">Connect Calls</CardTitle></CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-1.5">
                <Label>Internal Pre-Execution Call Date</Label>
                <p className="text-xs text-muted-foreground">Internal alignment call with both trainers before the event</p>
                <Input type="date" value={form.internal_call_date || ''} onChange={e => set('internal_call_date', e.target.value)}
                  className="max-w-xs" data-testid="rio-internal-call" />
              </div>
              <div className="space-y-1.5">
                <Label>External MS Team Call Date</Label>
                <p className="text-xs text-muted-foreground">Call with Microsoft team &amp; trainers (audience briefing, registration, local asks)</p>
                <Input type="date" value={form.ms_call_date || ''} onChange={e => set('ms_call_date', e.target.value)}
                  className="max-w-xs" data-testid="rio-ms-call" />
              </div>
              <div className="space-y-1.5">
                <Label>Notes</Label>
                <Textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)}
                  placeholder="Any additional notes..." rows={3} data-testid="rio-notes" />
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader><CardTitle className="text-base">Additional Fields</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <EntityCustomFields
              entityType="regional_in_person"
              values={form.custom_fields || {}}
              onChange={(v) => set('custom_fields', v)}
              isSuperAdmin={user?.role === 'superadmin'}
              currentUser={user}
              testPrefix="rio-custom-field"
            />
          </CardContent>
        </Card>

        <div className="flex justify-between pt-2">
          <div className="flex gap-2">
            {activeTab > 0 && (
              <Button type="button" variant="outline" onClick={() => setActiveTab(t => t - 1)}>← Previous</Button>
            )}
            {activeTab < TABS.length - 1 && (
              <Button type="button" variant="outline" onClick={() => setActiveTab(t => t + 1)}>Next →</Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-lime text-lime-foreground hover:bg-lime/90" data-testid="rio-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {isEdit ? 'Save Changes' : 'Create Event'}
            </Button>
          </div>
        </div>
      </form>
      <ContentItemLinkDialog
        open={contentItemDialogOpen}
        onOpenChange={setContentItemDialogOpen}
        onSelect={(item) => { setContentItemDialogOpen(false); setForm(f => ({ ...f, content_item_id: item?.id || '', content_item_title: item?.title || '' })); }}
      />
    </div>
  );
};

export default RegionalInPersonFormPage;
